import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Hammer, Users } from "lucide-react";
import { api } from "../lib/api";
import Reveal from "../components/site/Reveal";
import CountUp from "../components/site/CountUp";
import { useReveal } from "../lib/useReveal";

const tone = (status) => {
  if (status === "available") return { label: "#C9B79C", bar: "bg-[#C9B79C]", chip: "bg-[#C9B79C] text-[#12201A]" };
  if (status === "completed") return { label: "#3E7A5E", bar: "bg-[#3E7A5E]", chip: "bg-[#26402F] text-[#F3EEE3]" };
  return { label: "#5B6B7A", bar: "bg-[#5B6B7A]", chip: "bg-[#1B2E24] text-[#F3EEE3]/70" };
};

const Stages = () => {
  const [stages, setStages] = useState([]);
  useEffect(() => {
    api.get("/stages").then((r) => setStages(r.data)).catch(() => {});
  }, []);
  useReveal([stages.length]);

  return (
    <div className="pt-[64px] bg-[#12201A] min-h-screen">
      <div className="max-w-[1500px] mx-auto px-5 md:px-8 py-14">
        <p className="eyebrow text-[#C9B79C] mb-3">Development</p>
        <Reveal as="h1" className="font-display font-light text-4xl md:text-5xl text-[#F3EEE3]">Stages &amp; Phases</Reveal>
        <p className="text-[#F3EEE3]/60 mt-3 max-w-2xl">
          Deer Park Estates is delivered across <CountUp value={stages.length} /> stages. Stages 1–6 are built out, lots in Stages 7 and 9 are committed by
          builders, Stage 8 is now selling, and later stages are in development.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {stages.map((s, i) => {
            const t = tone(s.status);
            return (
              <Link
                key={s.id}
                to={s.link || `/stages/${s.id}`}
                data-testid={`stage-card-${s.number}`}
                style={{ transitionDelay: `${(i % 3) * 70}ms` }}
                className={`reveal group rounded-2xl border bg-[#0F1B15] p-6 transition-all hover:-translate-y-1 ${s.status === "available" ? "border-[#C9B79C]/60 shadow-[0_0_0_1px_#C9B79C]/20" : "border-[#F3EEE3]/12 hover:border-[#C9B79C]/40"}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-display text-4xl text-[#F3EEE3]/25">{String(s.number).padStart(2, "0")}</span>
                  <span className={`text-[0.6rem] tracking-wide uppercase px-2.5 py-1 rounded-full ${t.chip}`}>{s.status_label}</span>
                </div>
                <p className="font-display text-2xl text-[#F3EEE3] mt-3">{s.name}</p>
                <p className="text-[#F3EEE3]/55 text-sm mt-1">{s.parcels_count ? `${s.parcels_count} lots` : "—"}</p>
                {s.status !== "planning" && s.homes_underway > 0 && (
                  <div className="flex flex-wrap items-center gap-2 mt-3" data-testid={`stage-demand-${s.number}`}>
                    <span className="inline-flex items-center gap-1.5 text-[0.68rem] font-medium px-2.5 py-1 rounded-full bg-[#C9B79C]/15 text-[#C9B79C]">
                      <Hammer size={12} /> {s.homes_underway} homes underway
                    </span>
                    {s.builders_count > 0 && (
                      <span className="inline-flex items-center gap-1.5 text-[0.68rem] font-medium px-2.5 py-1 rounded-full bg-[#3E7A5E]/20 text-[#8FCBAA]">
                        <Users size={12} /> {s.builders_count} builder{s.builders_count > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                )}
                <div className="h-1.5 rounded-full bg-[#F3EEE3]/10 mt-4 overflow-hidden">
                  <div className={`h-full ${t.bar}`} style={{ width: s.status === "completed" ? "100%" : s.status === "available" ? "60%" : "20%" }} />
                </div>
                <span className="inline-flex items-center gap-1.5 text-[#C9B79C] text-xs mt-5 group-hover:gap-2.5 transition-all">
                  {s.link ? "View commercial zone" : "View stage"} <ArrowRight size={13} />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Stages;
